import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleRow } from "@/components/settings/shared/ToggleRow";
import { Flag, Loader2, Plus } from "lucide-react";

export interface FeatureFlagRow {
  key: string;
  enabled: boolean;
  description: string | null;
  updated_at: string;
}

const STALE_MS = 60 * 1000;

export function FeatureFlagsPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newKey, setNewKey] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [adding, setAdding] = useState(false);

  const { data: flags, isLoading } = useQuery({
    queryKey: ["founder-feature-flags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("feature_flags")
        .select("key, enabled, description, updated_at")
        .order("key", { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []) as FeatureFlagRow[];
    },
    staleTime: STALE_MS,
  });

  const persistToggle = async (key: string, enabled: boolean) => {
    const { error } = await supabase
      .from("feature_flags")
      .update({ enabled, updated_at: new Date().toISOString() })
      .eq("key", key);
    if (error) throw error;
    await queryClient.invalidateQueries({ queryKey: ["founder-feature-flags"] });
  };

  const addFlag = async (e: React.FormEvent) => {
    e.preventDefault();
    const k = newKey.trim().toLowerCase().replace(/\s+/g, "_");
    if (!k || !/^[a-z0-9_]{2,64}$/.test(k)) {
      toast({
        title: "Invalid key",
        description: "Use 2–64 characters: lowercase letters, numbers, underscores.",
        variant: "destructive",
      });
      return;
    }
    setAdding(true);
    try {
      const { error } = await supabase.from("feature_flags").upsert(
        {
          key: k,
          enabled: false,
          description: newDesc.trim() || null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "key" },
      );
      if (error) throw error;
      toast({ title: "Flag saved", description: k });
      setNewKey("");
      setNewDesc("");
      await queryClient.invalidateQueries({ queryKey: ["founder-feature-flags"] });
    } catch (err) {
      toast({
        title: "Could not add flag",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="founder-feature-flags-panel">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Flag className="h-5 w-5" /> Feature flags
                  </CardTitle>
                  <CardDescription>
                    Store toggles in the database for future rollout. Changes apply to all users once the app reads these keys.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8">
                  <form onSubmit={addFlag} className="rounded-lg border p-4 space-y-4 max-w-xl">
                    <h4 className="text-sm font-medium">Add flag</h4>
                    <div className="space-y-2">
                      <Label htmlFor="ff-key">Key</Label>
                      <Input
                        id="ff-key"
                        value={newKey}
                        onChange={(e) => setNewKey(e.target.value)}
                        placeholder="e.g. beta_reports_export"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ff-desc">Description (optional)</Label>
                      <Input
                        id="ff-desc"
                        value={newDesc}
                        onChange={(e) => setNewDesc(e.target.value)}
                        placeholder="What this flag controls"
                      />
                    </div>
                    <Button type="submit" variant="outline" size="sm" disabled={adding}>
                      {adding ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-2" />
                          Add or update key
                        </>
                      )}
                    </Button>
                  </form>

                  <div>
                    <h4 className="text-sm font-medium mb-2">Existing flags</h4>
                    {isLoading ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      </div>
                    ) : flags?.length ? (
                      <div className="rounded-lg border divide-y divide-border">
                        {flags.map((row) => (
                          <ToggleRow
                            key={row.key}
                            label={row.key}
                            description={row.description || "No description"}
                            checked={row.enabled}
                            onSave={async (v) => {
                              try {
                                await persistToggle(row.key, v);
                                toast({ title: "Saved" });
                              } catch (err) {
                                toast({
                                  title: "Could not save",
                                  description: err instanceof Error ? err.message : "Try again.",
                                  variant: "destructive",
                                });
                                throw err;
                              }
                            }}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No flags yet. Add one above.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
    </div>
  );
}
